import { getStandardButtonStyles } from "../../styles/standardButtons";
import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Compressor from "compressorjs";

import { i18n } from "../../translate/i18n";

import api, { getMediaUrl } from "../../services/api";
import toastError from "../../errors/toastError";
import { Checkbox, Stack } from "@mui/material";

const useStyles = makeStyles(theme => ({
  ...getStandardButtonStyles(theme),
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },

  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },

  btnWrapper: {
    position: "relative"
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const FlowBuilderAddAudioModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);

  const [loading, setLoading] = useState(false);

  const [record, setRecord] = useState(false);

  const [preview, setPreview] = useState();

  const [labels, setLabels] = useState({
    title: i18n.t("flowBuilder.audio.addTitle") || "Adicionar audio ao fluxo",
    btn: i18n.t("common.add") || "Adicionar"
  });

  const [name, setName] = useState("");
  const [caption, setCaption] = useState("");
  const [textDig, setTextDig] = useState();

  const [medias, setMedias] = useState([]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: i18n.t("flowBuilder.audio.editTitle") || "Editar audio",
        btn: i18n.t("common.save")
      });
      const companyId = localStorage.getItem("companyId") || 1;
      // [ANTIGRAVITY] Fix: Use centralized media URL helper
      setPreview(getMediaUrl(data.data.url, companyId));
      setRecord(data.data.record)
      setName(data.data.name || "");
      setCaption(data.data.caption || "");
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: i18n.t("flowBuilder.audio.addTitle") || "Adicionar audio ao fluxo",
        btn: i18n.t("common.add") || "Adicionar"
      });
      setTextDig("");
      setName("");
      setCaption("");
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSaveContact = async () => {
    if (open === "edit" && medias.length === 0) {
      handleClose();
      onUpdate({
        ...data,
        data: {
          ...data.data,
          name: name,
          caption: "", // Audio caption removed
          url: data.data.url ? data.data.url.split(/[/\\]/).pop() : "",
          record: record
        }
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("fromMe", true);
    formData.append("typeArch", "flow");

    medias.forEach((media) => {
      if (media) {
        formData.append("medias", media);
        formData.append("body", media.name);
      }
    });

    try {
      const { data: resData } = await api.post("/flowbuilder/audio", formData);
      handleClose();

      if (open === "edit") {
        onUpdate({
          ...data,
          data: {
            ...data.data,
            name: name,
            caption: "", // Audio caption removed
            url: resData.name ? resData.name.split(/[/\\]/).pop() : "",
            record: record
          }
        });
      } else {
        onSave({
          url: resData.name ? resData.name.split(/[/\\]/).pop() : "",
          name: name,
          caption: "" // Audio caption removed
        });
      }

      toast.success(i18n.t("flowBuilder.audio.success") || "Audio adicionado com sucesso!");
      setMedias([]);
      setPreview();
      setName("");
      setCaption("");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    if (e.target.files[0].size > 5000000) {
      toast.error(i18n.t("flowBuilder.audio.tooLarge") || "Arquivo é muito grande! 5MB máximo")
      return
    }

    const selectedMedias = Array.from(e.target.files);
    setPreview((URL.createObjectURL(e.target.files[0])));
    setMedias(selectedMedias);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Stack gap={"16px"}>
              <TextField
                label={i18n.t("flowBuilder.form.name") || "Nome"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                variant="outlined"
                margin="dense"
              />
              {/* CAPTION REMOVED FOR AUDIO */}
              {preview && (
                <Stack direction={'row'} justifyContent={'center'}>
                  <audio controls="controls" key={preview}>
                    <source src={preview} />
                    {i18n.t("flowBuilder.audio.browserSupport") || "seu navegador não suporta HTML5"}
                  </audio>
                </Stack>
              )}
              {preview && (
                <Stack direction={'row'} justifyContent={'center'}>
                  <Checkbox checked={record} onChange={(e) => setRecord(old => !old)} />
                  <Stack justifyContent={'center'}>
                    <Typography>{i18n.t("flowBuilder.audio.recordNow") || "Enviar como audio gravado na hora"}</Typography>
                  </Stack>
                </Stack>
              )}
              {!loading && (
                <>
                  <Button variant="contained" component="label">
                    {i18n.t("flowBuilder.audio.upload") || "Enviar audio"}
                    <input
                      type="file"
                      accept="audio/mp3, audio/ogg, audio/opus, audio/oga, audio/wav, audio/mpeg, audio/x-m4a, audio/aac"
                      disabled={loading}
                      hidden
                      onChange={handleChangeMedias}
                    />
                  </Button>
                  <Typography>{i18n.t("flowBuilder.audio.attention") || "ATENÇÃO! Apenas audios em MP3!"}</Typography>
                </>
              )}
              {loading && (
                <>
                  <Stack justifyContent={"center"} alignSelf={"center"}>
                    <CircularProgress />
                  </Stack>
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            {!loading && (
              <>
                <Button onClick={() => {
                    handleClose();
                    setMedias([]);
                    setPreview();
                    setCaption("");
                  }} 
                  className={classes.secondaryButton}
                >
                  {i18n.t("common.cancel") || "Cancelar"}
                </Button>
                <Button 
                  type="submit" 
                  onClick={() => handleSaveContact()} 
                  disabled={loading} 
                  className={classes.applyButton}
                >
                  {`${labels.btn}`}
                </Button>
              </>
            )}
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddAudioModal;
